import { useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getToken, qs } from '../lib/api';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
  Tabs,
  Td,
  Toggle,
} from '../components/ui/primitives';
import { dateTime, titleCase } from '../lib/format';
import type { ClientRecord } from '../lib/types';

interface ImportSummary {
  batchId: string;
  totalRows: number;
  imported: number;
  duplicates: number;
  suppressed: number;
  invalid: number;
  sampleErrors: string[];
}

interface ContactRow {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  status: string;
  verificationStatus: string;
  providerFamily: string | null;
  createdAt: string;
}

export function ContactsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('contacts');
  const [clientId, setClientId] = useState('');
  const [listId, setListId] = useState('');
  const [search, setSearch] = useState('');
  const [providerFamily, setProviderFamily] = useState('');
  const [newListName, setNewListName] = useState('');

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ clients: ClientRecord[] }>('/api/clients'),
  });

  const activeClient = clientId || clients?.clients[0]?.id || '';

  const { data: lists } = useQuery({
    queryKey: ['lists', activeClient],
    queryFn: () => api.get<{ lists: Array<{ id: string; name: string; contactCount: number }> }>(
      '/api/clients/' + activeClient + '/lists'
    ),
    enabled: Boolean(activeClient),
  });

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['contacts', activeClient, listId, search, providerFamily],
    queryFn: () =>
      api.get<{ rows: ContactRow[]; total: number }>(
        '/api/clients/' + activeClient + '/contacts' + qs({ listId, search, providerFamily, limit: 100 })
      ),
    enabled: Boolean(activeClient),
  });

  const { data: batches } = useQuery({
    queryKey: ['imports', activeClient],
    queryFn: () =>
      api.get<{ batches: Array<Record<string, string | number | null>> }>(
        '/api/clients/' + activeClient + '/imports'
      ),
    enabled: Boolean(activeClient),
  });

  const { data: suppressions } = useQuery({
    queryKey: ['suppressions', activeClient],
    queryFn: () =>
      api.get<{ rows: Array<{ id: string; email: string; reason: string; source: string | null; createdAt: string }>; total: number }>(
        '/api/suppressions' + qs({ clientId: activeClient, limit: 100 })
      ),
    enabled: Boolean(activeClient),
  });

  const createList = useMutation({
    mutationFn: () => api.post('/api/clients/' + activeClient + '/lists', { name: newListName }),
    onSuccess: () => {
      setNewListName('');
      void queryClient.invalidateQueries({ queryKey: ['lists', activeClient] });
    },
  });

  const verify = useMutation({
    mutationFn: () => api.post('/api/clients/' + activeClient + '/verify', { limit: 500 }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['contacts', activeClient] }),
  });

  if (!clients) return <Loading />;

  return (
    <>
      <PageHeader
        title="Contacts"
        description="Upload in batches. Every row is validated, de-duplicated and checked against suppression before it is stored, and the per-row outcome is kept so you can see exactly what a list vendor delivered."
        actions={
          <Button loading={verify.isPending} onClick={() => verify.mutate()} disabled={!activeClient}>
            Verify pending addresses
          </Button>
        }
      />

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <Field label="Client">
          <Select value={activeClient} onChange={(e) => setClientId(e.target.value)}>
            {clients.clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="List">
          <Select value={listId} onChange={(e) => setListId(e.target.value)}>
            <option value="">All lists</option>
            {(lists?.lists ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.contactCount})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Receiving provider">
          <Select value={providerFamily} onChange={(e) => setProviderFamily(e.target.value)}>
            <option value="">Any</option>
            <option value="microsoft">Microsoft</option>
            <option value="google">Google</option>
            <option value="yahoo">Yahoo</option>
            <option value="proofpoint">Proofpoint</option>
            <option value="mimecast">Mimecast</option>
            <option value="other">Other</option>
          </Select>
        </Field>
        <Field label="Search">
          <Input placeholder="email or company" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Field>
      </div>

      <Tabs
        tabs={[
          { id: 'contacts', label: 'Contacts' },
          { id: 'upload', label: 'Batch upload' },
          { id: 'lists', label: 'Lists' },
          { id: 'suppressions', label: 'Suppression list' },
          { id: 'imports', label: 'Import history' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'contacts' && (
        <Card subtitle={contacts ? contacts.total.toLocaleString() + ' contacts match' : undefined}>
          {isLoading ? (
            <Loading />
          ) : !contacts?.rows.length ? (
            <EmptyState title="No contacts" body="Upload a CSV to get started." />
          ) : (
            <Table head={['Email', 'Name', 'Company', 'Status', 'Verification', 'Receiving provider', 'Added']}>
              {contacts.rows.map((c) => (
                <tr key={c.id}>
                  <Td className="font-mono text-xs">{c.email}</Td>
                  <Td>{[c.firstName, c.lastName].filter(Boolean).join(' ') || '--'}</Td>
                  <Td>{c.company ?? '--'}</Td>
                  <Td>
                    <Badge tone={c.status === 'active' ? 'good' : c.status === 'bounced' ? 'bad' : 'neutral'}>
                      {c.status}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      tone={
                        c.verificationStatus === 'valid'
                          ? 'good'
                          : c.verificationStatus === 'invalid'
                            ? 'bad'
                            : c.verificationStatus === 'unverified'
                              ? 'warn'
                              : 'neutral'
                      }
                    >
                      {titleCase(c.verificationStatus)}
                    </Badge>
                  </Td>
                  <Td>
                    {c.providerFamily ? (
                      <Badge tone={c.providerFamily === 'microsoft' ? 'info' : 'neutral'}>
                        {titleCase(c.providerFamily)}
                      </Badge>
                    ) : (
                      <span className="text-xs text-ink-400">resolving</span>
                    )}
                  </Td>
                  <Td className="text-xs">{dateTime(c.createdAt)}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {tab === 'upload' && <UploadPanel clientId={activeClient} lists={lists?.lists ?? []} />}

      {tab === 'lists' && (
        <Card
          title="Lists"
          actions={
            <div className="flex items-end gap-2">
              <Input
                placeholder="New list name"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
              />
              <Button loading={createList.isPending} disabled={!newListName} onClick={() => createList.mutate()}>
                Create
              </Button>
            </div>
          }
        >
          {!lists?.lists.length ? (
            <EmptyState title="No lists yet" />
          ) : (
            <Table head={['List', 'Contacts']}>
              {lists.lists.map((l) => (
                <tr key={l.id}>
                  <Td className="font-medium text-ink-900">{l.name}</Td>
                  <Td className="tabular-nums">{l.contactCount.toLocaleString()}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {tab === 'suppressions' && (
        <Card
          title="Suppression list"
          subtitle="Enforced at plan time and again at send time. A complaint suppresses the address across every client; everything else is scoped to this client."
        >
          {!suppressions?.rows.length ? (
            <EmptyState title="Nothing suppressed" />
          ) : (
            <Table head={['Email', 'Reason', 'Source', 'Added']}>
              {suppressions.rows.map((s) => (
                <tr key={s.id}>
                  <Td className="font-mono text-xs">{s.email}</Td>
                  <Td>
                    <Badge tone={s.reason === 'complaint' ? 'bad' : 'neutral'}>{titleCase(s.reason)}</Badge>
                  </Td>
                  <Td className="text-xs text-ink-500">{s.source ?? '--'}</Td>
                  <Td className="text-xs">{dateTime(s.createdAt)}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {tab === 'imports' && (
        <Card title="Import history">
          {!batches?.batches.length ? (
            <EmptyState title="No imports yet" />
          ) : (
            <Table head={['File', 'Status', 'Rows', 'Imported', 'Duplicates', 'Suppressed', 'Invalid', 'When']}>
              {batches.batches.map((b) => (
                <tr key={String(b.id)}>
                  <Td className="font-medium text-ink-900">{String(b.filename)}</Td>
                  <Td>
                    <Badge tone={b.status === 'completed' ? 'good' : 'info'}>{String(b.status)}</Badge>
                  </Td>
                  <Td className="tabular-nums">{Number(b.total_rows).toLocaleString()}</Td>
                  <Td className="tabular-nums">{Number(b.imported).toLocaleString()}</Td>
                  <Td className="tabular-nums">{Number(b.duplicates).toLocaleString()}</Td>
                  <Td className="tabular-nums">{Number(b.suppressed).toLocaleString()}</Td>
                  <Td className="tabular-nums">{Number(b.invalid).toLocaleString()}</Td>
                  <Td className="text-xs">{dateTime(String(b.created_at))}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}
    </>
  );
}

/** Batch upload with the per-row outcome shown immediately. */
function UploadPanel({
  clientId,
  lists,
}: {
  clientId: string;
  lists: Array<{ id: string; name: string }>;
}) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [listId, setListId] = useState('');
  const [source, setSource] = useState('csv_upload');
  const [consentBasis, setConsentBasis] = useState('legitimate_interest');
  const [verify, setVerify] = useState(true);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function upload() {
    if (!file) return;
    setBusy(true);
    setError(null);
    const body = new FormData();
    body.append('listId', listId);
    body.append('source', source);
    body.append('consentBasis', consentBasis);
    body.append('verify', String(verify));
    body.append('file', file);

    try {
      const response = await fetch('/api/clients/' + clientId + '/imports', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + getToken() },
        body,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json?.error?.message ?? 'Upload failed');
      setResult(json as ImportSummary);
      void queryClient.invalidateQueries({ queryKey: ['contacts', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['imports', clientId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <Card
        title="Upload a CSV"
        subtitle="Column headers are matched automatically: email, first name, last name, company, title, phone. Anything else is kept as a custom field you can merge into the message."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="File">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-ink-600 file:mr-3 file:rounded-md file:border-0 file:bg-ink-900 file:px-3 file:py-2 file:text-sm file:text-white"
            />
          </Field>
          <Field label="Add to list">
            <Select value={listId} onChange={(e) => setListId(e.target.value)}>
              <option value="">No list</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Source" hint="Recorded against every contact for audit.">
            <Input value={source} onChange={(e) => setSource(e.target.value)} />
          </Field>
          <Field label="Lawful basis">
            <Select value={consentBasis} onChange={(e) => setConsentBasis(e.target.value)}>
              <option value="legitimate_interest">Legitimate interest</option>
              <option value="consent">Consent</option>
              <option value="contract">Contract</option>
            </Select>
          </Field>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <Toggle checked={verify} onChange={setVerify} label="Queue verification after import" />
          <Button variant="primary" loading={busy} disabled={!file || !clientId} onClick={() => void upload()}>
            Upload and process
          </Button>
        </div>

        {error && (
          <div className="mt-4">
            <Callout tone="bad">{error}</Callout>
          </div>
        )}
      </Card>

      {result && (
        <Card title="Import result">
          <div className="grid gap-4 sm:grid-cols-5">
            <Summary label="Rows in file" value={result.totalRows} />
            <Summary label="Imported" value={result.imported} tone="good" />
            <Summary label="Duplicates" value={result.duplicates} />
            <Summary label="Suppressed" value={result.suppressed} tone="warn" />
            <Summary label="Invalid" value={result.invalid} tone="bad" />
          </div>
          {result.sampleErrors.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
                First rejected rows
              </p>
              <ul className="space-y-1 font-mono text-[11px] text-ink-600">
                {result.sampleErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function Summary({ label, value, tone }: { label: string; value: number; tone?: 'good' | 'warn' | 'bad' }) {
  const colour =
    tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : tone === 'bad' ? 'text-red-700' : 'text-ink-900';
  return (
    <div className="rounded-lg border border-ink-200 px-4 py-3">
      <div className="text-xs text-ink-500">{label}</div>
      <div className={'mt-1 text-xl font-semibold tabular-nums ' + colour}>{value.toLocaleString()}</div>
    </div>
  );
}
